import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Paper,
  Typography,
  Container,
  Grid,
  Button,
  CircularProgress,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ValidatedPasswordInput } from 'components/ValidatedPasswordInput';
import logo from 'static/media/logo2x.png';
import 'translations/translations';

import { ApiService } from 'services/apiService';
import { userActions } from 'actions/userActions';
import { useDispatch } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: '8%',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '25rem',
    margin: 'auto',
    padding: '1.8rem 3.125rem',
  },
  img: {
    maxWidth: '18rem',
    marginBottom: '2rem',
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(6, 0, 1, 0),
    paddingRight: theme.spacing(5),
    paddingLeft: theme.spacing(5),
    color: '#fff',
    fontWeight: 'bold',
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
  alignCenter: {
    textAlign: 'center',
  },
}));

const ConfirmAccount = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const query = new URLSearchParams(useLocation().search);
  const email = query.get('email');
  const token = query.get('token');

  const [confirmationSuccess, setConfirmationSuccess] = useState<
    boolean | null
  >(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');

  const { t } = useTranslation(['translations']);

  const handleChange = (name: string, value: string) => {
    setPassword(value);
  };

  const isInputValid = password.length > 0;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (email && token) {
      setConfirmationSuccess(false); // signal submit in progress
      ApiService.confirmAccount(email, password, token)
        .then(() => {
          setConfirmationSuccess(true);
        })
        .catch((error) => {
          setConfirmationSuccess(null); // switch off submit signal
          setError(error.response?.data?.errorMessage);
        });
    }
  };

  const handleLogout = () => {
    dispatch(userActions.logout());
  };

  return (
    <Container className={classes.container}>
      <Paper className={classes.paper}>
        <img src={logo} alt="Logo" className={classes.img} />
        {confirmationSuccess ? (
          <>
            <div>{t('confirm_account_page.confirmation_success')} </div>
            <Link href="/" onClick={handleLogout}>
              {t('confirm_account_page.proceed_to_login')}
            </Link>
            <Link href="https://mobile.hdlogsystems.com">
              {t('confirm_account_page.proceed_to_app_login')}
            </Link>
          </>
        ) : confirmationSuccess === false ? (
          <CircularProgress color="secondary" />
        ) : (
          <form className={classes.form} onSubmit={(e) => handleSubmit(e)}>
            <Typography component="h1" variant="h5">
              {t('confirm_account_page.title')} {email}
            </Typography>

            <ValidatedPasswordInput
              inputName="password"
              handleChangeCallback={handleChange}
              autoFocus={true}
            />
            <Typography className={classes.error}>
              {error && `${error}: ${t('login_page.errors.login_failed')}`}
            </Typography>
            <Grid container>
              <Grid item xs={12} className={classes.alignCenter}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={!isInputValid}
                >
                  {t('common.submit')}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>
    </Container>
  );
};

export { ConfirmAccount };
